import {useState, useCallback} from 'react';

/**
 * Custom hook to manage state with localStorage persistence.
 * @param key The localStorage key to store the state.
 * @param initialValue The initial state value.
 * @returns [state, setState] - The current state and a function to update it.
 */
function useLocalStorageState<T>(
   key: string,
   initialValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
   // Initialize state from localStorage or fallback to initial value
   const [state, setState] = useState<T>(() => {
      try {
         const storedValue = localStorage.getItem(key);
         return storedValue !== null ? JSON.parse(storedValue) : initialValue;
      } catch (error) {
         console.error(`Error parsing localStorage key "${key}":`, error);
         return initialValue;
      }
   });

   // Memoize the function that updates state and localStorage
   const setPersistedState = useCallback<React.Dispatch<React.SetStateAction<T>>>(
      (value) => {
         try {
            const newValue = value instanceof Function ? value(state) : value;
            setState(newValue); // Update state
            localStorage.setItem(key, JSON.stringify(newValue)); // Persist to localStorage
         } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
         }
      },
      [key, state] // Dependencies for useCallback
   );

   return [state, setPersistedState];
}

export default useLocalStorageState;
